import { Tooltip } from '../tooltips/Tooltip'
import { faCopy } from '@fortawesome/pro-light-svg-icons/faCopy'
import { faPencil } from '@fortawesome/pro-light-svg-icons/faPencil'
import { faShare } from '@fortawesome/pro-light-svg-icons/faShare'
import { faShareAlt } from '@fortawesome/pro-light-svg-icons/faShareAlt'
import { faTrash } from '@fortawesome/pro-light-svg-icons/faTrash'
import Button from '@mui/material/Button'
import ButtonGroup from '@mui/material/ButtonGroup'
import Stack, { StackProps } from '@mui/material/Stack'
import { useTranslation } from 'next-i18next'
import React from 'react'
import Icon from '@/components/icons/Icon'
import { Project } from '@/interfaces/projects'
import {
  getProjectCopyHref,
  getProjectDeleteHref,
  getProjectEditHref,
  getProjectShareHref,
  getWorkbenchHref
} from '@/helpers/uri'

interface ActionButtonProps {
  href: string
  icon: JSX.Element
  title: string
  'qa-attribute'?: string
}

interface ActionButtonsProps extends StackProps {
  project: Project
}

const ActionButton = ({ href, icon, title, ...props }: ActionButtonProps): React.ReactElement => (
  <Tooltip title={title}>
    <Button
      component="a"
      href={href}
      sx={{ padding: 3, whiteSpace: 'nowrap' }}
      disableElevation
      rel="nofollow"
      {...props}
    >
      {icon}
    </Button>
  </Tooltip>
)

export const ActionButtons = ({ project, ...props }: ActionButtonsProps): React.ReactElement => {
  const { t } = useTranslation('subheader')
  const permissions: Project['requesterPermissions'] = project.requesterPermissions

  return (
    <Stack direction="row" spacing={2} {...props} display={{ xs: 'none', sm: 'flex' }}>
      <Tooltip title={t('OpenProjectButtonTooltip')}>
        <Button
          variant="contained"
          color="primary"
          href={getWorkbenchHref(project.projectIdExt)}
          disableElevation
          endIcon={<Icon icon={faShare} />}
          qa-attribute="openInWorkbenchButton"
          sx={{ whiteSpace: 'nowrap' }}
        >
          {t('openProject')}
        </Button>
      </Tooltip>
      <ButtonGroup color="primary" variant="outlined">
        {permissions?.canShareProjectWithAnyone && (
          <ActionButton
            href={getProjectShareHref(project.projectIdExt)}
            icon={<Icon icon={faShareAlt} />}
            title={t('share')}
            qa-attribute="shareProjectButton"
          />
        )}
        {permissions?.canCopyProject && permissions?.canManageProject && (
          <ActionButton
            href={getProjectCopyHref(project.projectIdExt)}
            icon={<Icon icon={faCopy} />}
            title={t('copy')}
            qa-attribute="copyProjectButton"
          />
        )}
        {permissions?.canCopyProject && !permissions?.canManageProject && (
          <Tooltip title={t('copyProjectButtonTooltip')}>
            <Button
              variant="outlined"
              href={getProjectCopyHref(project.projectIdExt)}
              disableElevation
              endIcon={<Icon icon={faCopy} />}
              qa-attribute="largeCopyProjectButton"
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('copyProject')}
            </Button>
          </Tooltip>
        )}
        {permissions?.canManageProject && (
          <ActionButton
            href={getProjectEditHref(project.projectIdExt)}
            icon={<Icon icon={faPencil} />}
            title={t('edit')}
            qa-attribute="editProjectButton"
          />
        )}
        {permissions?.canDeleteProject && (
          <ActionButton
            href={getProjectDeleteHref(project.projectIdExt)}
            icon={<Icon icon={faTrash} />}
            title={t('delete')}
            qa-attribute="deleteProjectButton"
          />
        )}
      </ButtonGroup>
    </Stack>
  )
}
